import Config from "../constants/Config.js";
import keyValueToURLParams from "../utils/keyValueToURLParams";
import { wordpressFetch, getUserNonce } from "./index";

import { LoginFormType, RegisterFormType } from "../types/UserTypes";

export const userLogin = (profile: LoginFormType): Promise<any> => {
    let url = `${Config.userAPI}/email-login`;
    return wordpressFetch("POST", url, null, profile);
};

export const userSsoLogin = (token: string): Promise<any> => {
    let url = `${Config.userAPI}/sso-login`;
    return wordpressFetch("POST", url, null, { token: token });
};

export const userSsoSwitch = (token: string): Promise<any> => {
    let url = `${Config.userAPI}/switch-to-sso`;
    return wordpressFetch("POST", url, null, { token: token }).then(() => {
        return getUserNonce(true);
    });
};

export const userRegister = (profile: RegisterFormType): Promise<any> => {
    let url = `${Config.userAPI}/email-signup`;
    return wordpressFetch("POST", url, null, profile);
};

export const userLogout = (): Promise<any> => {
    let url = `${Config.userAPI}/logout`;
    return wordpressFetch("POST", url);
};

export const userGetProfile = (): Promise<any> => {
    let url = `${Config.userAPI}/me`;
    return wordpressFetch("GET", url);
};

export const userGetOptIns = (): Promise<any> => {
    let url = `${Config.userAPI}/me/optins`;
    return wordpressFetch("GET", url);
};

export const userGetMyMemberships = (): Promise<any> => {
    let url = `${Config.userAPI}/my-memberships`;
    return wordpressFetch("GET", url);
};

// For Club execs
export const userGetMyClubs = (): Promise<any> => {
    let url = `${Config.userAPI}/my-managed-clubs`;
    return wordpressFetch("GET", url);
};

export const userGetMyClubEvents = ({
    id,
    type = "upcoming",
    page = 1,
    perPage = 20,
}: {
    id: number;
    type: "past" | "upcoming";
    page?: number;
    perPage?: number;
}): Promise<any> => {
    let params: any = {
        per_page: perPage,
        page: page,
        type: type,
    };

    const urlParams = keyValueToURLParams(params);
    let url = `${Config.userAPI}/my-managed-clubs/${id}/event-list?${urlParams}`;
    return wordpressFetch("GET", url, null, null, true);
};

export const userGetManagedClubMembers = (id: number): Promise<any> => {
    let url = `${Config.userAPI}/my-managed-clubs/${id}/member-list`;
    return wordpressFetch("GET", url);
};

export const userGetManagedEvent = (id: number): Promise<any> => {
    let url = `${Config.userAPI}/my-managed-events/${id}`;
    return wordpressFetch("GET", url);
};

export const userGetManagedEventAttendees = (id: number): Promise<any> => {
    let url = `${Config.userAPI}/my-managed-events/${id}/registration-list`;
    return wordpressFetch("GET", url);
};

export const userUploadManagedEventMedia = (
    id: number,
    formData: any
): Promise<any> => {
    let url = `${Config.userAPI}/my-managed-events/${id}/media`;

    const headers = new Headers();
    // headers.append("Content-Type", "multipart/form-data");

    return wordpressFetch("POST", url, headers, formData, undefined, true);
};

export const userUpdateManagedEvent = (
    id: number,
    formData: any
): Promise<any> => {
    let url = `${Config.userAPI}/my-managed-events/${id}`;
    return wordpressFetch("POST", url, null, formData);
};

export const userUpdateImmediateManagedEvent = (
    id: number,
    formData: any
): Promise<any> => {
    let url = `${Config.userAPI}/my-managed-events/publish/${id}`;
    return wordpressFetch("POST", url, null, formData);
};

export const userUpdateManagedEventAttendees = (
    id: number,
    data: {
        reported_attendance_figure: number;
        reported_student_attendance: number;
    }
): Promise<any> => {
    let url = `${Config.userAPI}/my-managed-events/${id}/attendance`;
    return wordpressFetch("POST", url, null, data);
};

export const userCheckInEventTicket = (
    eventId: number,
    registrationId?: string,
    qrCode?: string
): Promise<any> => {
    let url;

    if (registrationId) {
        url = `${Config.userAPI}/my-managed-events/${eventId}/check-in?event_registration_id=${registrationId}`;
    } else {
        url = `${Config.userAPI}/my-managed-events/${eventId}/check-in?event_qr_code=${qrCode}`;
    }

    return wordpressFetch("POST", url);
};

export const userGetMyTickets = ({
    perPage = 9,
    page = 1,
    type = "past",
}: {
    perPage?: number;
    page?: number;
    type: "past" | "upcoming";
}): Promise<any> => {
    let params: any = {
        per_page: perPage,
        page: page,
        type: type,
    };

    const urlParams = keyValueToURLParams(params);
    let url = `${Config.userAPI}/my-tickets?${urlParams}`;
    return wordpressFetch("GET", url, null, null, true);
};

export const userGetPurchasedEvents = (profile: any): Promise<any> => {
    let url = `${Config.userAPI}/purchased-events`;
    return wordpressFetch("GET", url);
};

export const userGetOrderDetails = (id?: number): Promise<any> => {
    let url = `${Config.userAPI}/my-orders` + (id ? `/${id}` : "");
    return wordpressFetch("GET", url);
};

export const userGetMyOrders = ({
    perPage = 9,
    page = 1,
    queryParms,
}: {
    perPage?: number;
    page?: number;
    queryParms?: {
        page_num?: any;
    };
}): Promise<any> => {
    let params: any = {
        per_page: perPage,
        page: queryParms && queryParms.page_num ? queryParms.page_num : page,
    };

    const urlParams = keyValueToURLParams(params);
    let url = `${Config.userAPI}/my-orders?${urlParams}`;
    return wordpressFetch("GET", url, null, null, true);
};

export const userForgotPassword = (
    email: string,
    recaptchaResponse: string
): Promise<any> => {
    let url = `${Config.userAPI}/forgot-password`;
    return wordpressFetch("POST", url, null, {
        email: email,
        recaptcha_token: recaptchaResponse,
    });
};

export const userResetPassword = (
    token: string,
    email: string,
    password: string,
    recaptchaResponse: string
): Promise<any> => {
    let url = `${Config.userAPI}/reset-password`;
    return wordpressFetch("POST", url, null, {
        token: token,
        login: email,
        password: password,
        recaptcha_token: recaptchaResponse,
    });
};

export const userChangePassword = (
    current_password: string,
    new_password: string
): Promise<any> => {
    let url = `${Config.userAPI}/change-password`;
    return wordpressFetch("POST", url, null, {
        current_password: current_password,
        new_password: new_password,
    }).then(() => {
        return getUserNonce(true);
    });
};

export const updateUserProfile = (profile: any): Promise<any> => {
    let url = `${Config.userAPI}/me`;
    return wordpressFetch("POST", url, null, profile).then(() => {
        return getUserNonce(true);
    });
};

export const updateUserOptins = (optins: any): Promise<any> => {
    let url = `${Config.userAPI}/me/optins`;
    return wordpressFetch("POST", url, null, { optins: optins });
};

const user = {
    userLogin,
    userSsoLogin,
    userSsoSwitch,
    userRegister,
    userLogout,
    userGetProfile,
    userGetOptIns,
    userGetMyMemberships,
    userGetMyOrders,
    userGetOrderDetails,
    userGetMyClubs,
    userGetMyClubEvents,
    userGetManagedClubMembers,
    userGetManagedEvent,
    userGetManagedEventAttendees,
    userUploadManagedEventMedia,
    userUpdateManagedEvent,
    userUpdateManagedEventAttendees,
    userCheckInEventTicket,
    userGetMyTickets,
    userGetPurchasedEvents,
    userForgotPassword,
    userResetPassword,
    updateUserProfile,
    updateUserOptins,
    userChangePassword,
    userUpdateImmediateManagedEvent
};

export default user;
